/**
 * Utilitário para manipulação de URLs e abertura de links
 * @module url-handler
 */

/**
 * Verifica se uma URL é válida
 * @param url URL a ser validada
 * @returns {boolean} true se a URL for válida, false caso contrário
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Abre uma URL externa com tratamento de erros e fallback
 * @param url URL a ser aberta
 * @param target Alvo da abertura (_blank, _self, etc)
 * @returns {Promise<boolean>} true se a URL foi aberta com sucesso, false caso contrário
 */
export async function openExternalUrl(url: string, target: string = '_blank'): Promise<boolean> {
  try {
    // Verifica se a URL é válida
    if (!isValidUrl(url)) {
      console.error('URL inválida:', url);
      return false;
    }

    // Verifica se estamos em um ambiente Capacitor (app nativo)
    if (typeof (window as any).Capacitor !== 'undefined') {
      const { Browser } = (window as any).Capacitor.Plugins;
      
      // Abre a URL no navegador nativo
      await Browser.open({ url, windowName: '_system' });
      return true;
    }
    
    // Verifica se estamos em um ambiente React Native
    if (typeof (window as any).ReactNativeWebView !== 'undefined') {
      // Envia mensagem para o WebView do React Native
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'openUrl', url })
      );
      return true;
    }

    // Fallback para o método padrão do navegador
    const newWindow = window.open(url, target);
    
    // Verifica se o popup foi bloqueado
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      console.warn('Popup bloqueado pelo navegador. Tentando método alternativo...');
      
      // Tenta abrir usando location.href como último recurso
      if (target === '_self') {
        window.location.href = url;
      } else {
        // Informa o usuário que o popup foi bloqueado
        alert('O navegador bloqueou a abertura do link. Por favor, permita popups para este site ou clique manualmente no link.');
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao abrir URL externa:', error);
    
    // Tenta método alternativo em caso de erro
    try {
      window.location.href = url;
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Abre um link interno da aplicação com tratamento adequado
 * @param path Caminho interno da aplicação
 * @param navigate Função de navegação (useNavigate do react-router-dom)
 */
export function openInternalLink(path: string, navigate: (path: string) => void): void {
  try {
    // Remove barras iniciais duplicadas
    const normalizedPath = path.replace(/^\/+/, '/');
    navigate(normalizedPath);
  } catch (error) {
    console.error('Erro ao navegar para rota interna:', error);
    
    // Fallback para método tradicional
    window.location.href = path;
  }
}

/**
 * Determina se um link deve ser aberto internamente ou externamente
 * @param url URL ou caminho a ser analisado
 * @returns {boolean} true se deve ser tratado como link externo, false para interno
 */
export function isExternalLink(url: string): boolean {
  // Se começar com http:// ou https://, é externo
  if (/^https?:\/\//i.test(url)) {
    // Verifica se é do mesmo domínio
    try {
      const urlObj = new URL(url);
      const currentDomain = window.location.hostname;
      
      // Se for do mesmo domínio, é interno
      return urlObj.hostname !== currentDomain;
    } catch {
      return true; // Se não conseguir analisar, trata como externo
    }
  }
  
  // Se começar com /, é interno
  if (url.startsWith('/')) {
    return false;
  }
  
  // Se não tiver protocolo nem começar com /, assume externo
  return true;
}

/**
 * Manipula um clique em link, determinando se deve abrir interna ou externamente
 * @param url URL ou caminho a ser aberto
 * @param navigate Função de navegação (useNavigate do react-router-dom)
 * @param target Alvo da abertura (_blank, _self, etc)
 * @returns {Promise<boolean>} true se o link foi processado com sucesso
 */
export async function handleLinkClick(
  url: string, 
  navigate: (path: string) => void,
  target: string = '_blank'
): Promise<boolean> {
  if (!url) return false;
  
  if (isExternalLink(url)) {
    return await openExternalUrl(url, target);
  } else {
    openInternalLink(url, navigate);
    return true;
  }
}